import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { label: "Home", name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "" };
const input = "w-full h-10 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-3 text-sm";
const COUNTRIES = [["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"], ["AU", "Australia"]];

const F = ({ label, children, span }) => (
  <label className={`block ${span ? "col-span-2" : ""}`}><span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">{label}</span><div className="mt-1">{children}</div></label>
);

export const AddressDialog = ({ open, onOpenChange, address }) => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(address ? { ...EMPTY, ...address } : { ...EMPTY, name: user?.name || "" }); }, [address, open, user?.name]);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = { ...f }; delete body.id; delete body.is_default;
    try {
      const { data } = address ? await api.put(`/auth/addresses/${address.id}`, body) : await api.post("/auth/addresses", body);
      setUser(data); toast.success(address ? "Address updated" : "Address saved"); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="address-dialog" className="bg-[#121215] border-white/10 max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display uppercase font-black text-2xl">{address ? "Edit address" : "New address"}</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Saved addresses can be picked at checkout.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <F label="Label"><input data-testid="address-label-input" className={input} value={f.label} onChange={on("label")} placeholder="Home, Gym, Work" /></F>
          <F label="Full name"><input required data-testid="address-name-input" className={input} value={f.name} onChange={on("name")} /></F>
          <F label="Address line 1" span><input required data-testid="address-line1-input" className={input} value={f.line1} onChange={on("line1")} /></F>
          <F label="Address line 2" span><input data-testid="address-line2-input" className={input} value={f.line2} onChange={on("line2")} placeholder="Apt, suite (optional)" /></F>
          <F label="City"><input required data-testid="address-city-input" className={input} value={f.city} onChange={on("city")} /></F>
          <F label="State / Region"><input data-testid="address-state-input" className={input} value={f.state} onChange={on("state")} /></F>
          <F label="Postal code"><input required data-testid="address-postal-input" className={input} value={f.postal_code} onChange={on("postal_code")} /></F>
          <F label="Country">
            <select data-testid="address-country-select" className={input} value={f.country} onChange={on("country")}>
              {COUNTRIES.map(([c, l]) => <option key={c} value={c} className="bg-black">{l}</option>)}
            </select>
          </F>
          <F label="Phone (optional)" span><input data-testid="address-phone-input" className={input} value={f.phone} onChange={on("phone")} /></F>
          <button data-testid="address-submit" disabled={saving} className="col-span-2 h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider disabled:opacity-60">
            {saving ? "Saving..." : "Save address"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
